import { permissions } from "../config/permissions";
import { checkPermission } from "../config/utils";

const ALLOWANCE_PREFIX = "ALLOWANCE:";

// Absent reads as denied, so the verb rule stays off until the flag is granted.
export const isVerbValidationEnabled = () => checkPermission("feature_flag", permissions.RECOMMENDATION_VERB_VALIDATION, "enable");

const SUGGESTION_LIMIT = 40;

const toVerbNames = verbList =>
    (verbList || [])
        .map(verb => (typeof verb === "string" ? verb : verb?.name))
        .filter(name => typeof name === "string" && name.trim().length)
        .map(name => name.trim());

/** The governed word: after "ALLOWANCE:" when present, else the first word. */
export const getVerbToken = text => {
    const value = text || "";
    const hasAllowancePrefix = value.trimStart().startsWith(ALLOWANCE_PREFIX);
    const offset = hasAllowancePrefix ? value.indexOf(ALLOWANCE_PREFIX) + ALLOWANCE_PREFIX.length : 0;
    const rest = value.slice(offset);
    const leadingSpaces = rest.length - rest.trimStart().length;
    const start = offset + leadingSpaces;
    const word = value.slice(start).split(/\s/)[0] || "";
    return {
        prefix: hasAllowancePrefix ? ALLOWANCE_PREFIX : "",
        word,
        start,
        end: start + word.length
    };
};

export const matchVerbs = (typed, verbList, hasAllowancePrefix = false) => {
    if (!isVerbValidationEnabled()) return [];
    const value = (typed || "").trim().toLowerCase();
    if (!value.length) return [];
    return toVerbNames(verbList)
        .filter(name => !(hasAllowancePrefix && name.toUpperCase().startsWith(ALLOWANCE_PREFIX)))
        .filter(name => name.toLowerCase().startsWith(value))
        .sort((a, b) => a.localeCompare(b));
};

export const applyVerbToText = (text, verb) => {
    const value = text || "";
    const { prefix, start, end } = getVerbToken(value);
    if (prefix && verb.toUpperCase().startsWith(ALLOWANCE_PREFIX)) {
        return `${verb} ${value.slice(start)}`.trimEnd();
    }
    return `${value.slice(0, start)}${verb}${value.slice(end)}`;
};

const isApprovedVerb = (word, verbList) => {
    const value = (word || "").trim().toLowerCase();
    if (!value.length) return false;
    return toVerbNames(verbList).some(name => name.toLowerCase() === value);
};

export const isFirstWordValid = (text, verbList) => {
    if (!isVerbValidationEnabled()) return true;
    const { word } = getVerbToken(text);
    return isApprovedVerb(word, verbList) && /^[A-Z]/.test(word);
};

/** An empty verb list means "not loaded" - a failed fetch must not block every save. */
export const validateFirstWord = (text, verbList, label = "Recommendation") => {
    if (!isVerbValidationEnabled()) return null;
    const value = (text || "").trim();
    if (!value.length) return null;
    if (!toVerbNames(verbList).length) return null;

    const { prefix, word } = getVerbToken(text);
    const position = prefix ? `The word after ${ALLOWANCE_PREFIX}` : `The first word of the ${label.toLowerCase()}`;

    if (!word.length || !isApprovedVerb(word, verbList)) {
        return `* ${position} must be an approved verb.`;
    }
    if (!/^[A-Z]/.test(word)) {
        return `* ${position} must start with a capital letter.`;
    }
    return null;
};

/** Stays silent until the word is settled, so it never shouts at a half-typed word. */
export const getLiveVerbError = (text, verbList, label = "Recommendation") => {
    if (!isVerbValidationEnabled()) return null;
    const value = text || "";
    if (!value.trim().length) return null;
    if (!toVerbNames(verbList).length) return null;

    const { prefix, word, end } = getVerbToken(value);
    if (!word.length) return null;

    const isCompleteVerb = isApprovedVerb(word, verbList);
    const settled = value.length > end || isCompleteVerb || !matchVerbs(word, verbList, !!prefix).length;
    if (!settled) return null;

    return validateFirstWord(value, verbList, label);
};

export const matchTemplatesByPrefix = (typed, templates) => {
    const value = (typed || "").trim().toLowerCase();
    if (!value.length) return [];
    return (templates || [])
        .filter(item => item?.text_format?.toString()?.toLowerCase()?.startsWith(value))
        .slice(0, SUGGESTION_LIMIT);
};

export const getVerbFormatError = name => {
    const value = (name || "").trim();
    if (!value.length) return "Please enter a verb";
    if (/\s/.test(value)) return "A verb must be a single word";
    if (!/^[A-Z]/.test(value)) return "A verb must start with a capital letter";
    return null;
};
